import { FormSectionHeader, Loader, Slider } from "@/components";
import {
  BadgeDollarSign,
  MailOpen,
  Megaphone,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import PDFIcon from "@/assets/pdfIcon.svg";
import { Card, CardContent } from "@/components/ui";
import moment from "moment";
import useNewsLetterCard from "./useNewsLetterCard";
import { cn, formatCurrencyShort } from "@/lib/utils";
import dollarIcon from "@/assets/dollarIcon.png";
import dollarIconWhite from "@/assets/dollarIconWhite.png";
import noFlashNews from "@/assets/noFlashNews.png";
import { images } from "@/assets";

const NewsLetterCard = () => {
  const {
    files,
    cards,
    flashNews,
    onNewsletterClick,
    isPrevYearsCountsLoading,
    isLoadingNewsLoading,
    prevYearsCounts,
    currentFinanceYear,
    isBom,
  } = useNewsLetterCard();

  return (
    <div className="flex flex-col gap-[20px]">
      {isPrevYearsCountsLoading || isLoadingNewsLoading ? (
        <Loader />
      ) : (
        <>
          <div className="bg-[#FFEAEA] rounded-lg p-[20px] text-[12px]">
            <FormSectionHeader>
              <div className="flex flex-row items-center">
                <Megaphone className="mr-2" /> Flash News
              </div>
            </FormSectionHeader>
            {flashNews?.length ? (
              <Slider
                hideNextPrevButton
                autoSlide
                slides={flashNews?.map((data: any) => (
                  <div className={cn("w-full flex-shrink-0 min-h-[150px]")}>
                    <div>{data?.text}</div>
                    <br />
                    <span className="text-g-300">
                      Expires On:{" "}
                      {data?.expiry
                        ? moment(data?.expiry).format("MM/DD/YYYY")
                        : ""}{" "}
                    </span>
                  </div>
                ))}
              />
            ) : (
              <div className="flex items-center justify-center">
                <img src={noFlashNews} />
              </div>
            )}
          </div>
          <div
            className={cn(
              "bg-white rounded-lg p-[20px] row-span-[50]",
              isBom ? "" : "lg:pb-[41%]"
            )}
          >
            <div>
              <FormSectionHeader>
                <div className="flex flex-row items-center">
                  <MailOpen className="mr-2" /> Newsletter
                </div>
              </FormSectionHeader>

              {files?.length ? (
                <div className="grid grid-cols-1 gap-[10px]">
                  {files?.map((file, index: number) => (
                    <>
                      <span className="text-gray-500 text-[12px]">
                        Newsletter {index + 1}
                      </span>
                      <div className="flex flex-row gap-2 items-center">
                        {file?.name ? (
                          <img src={PDFIcon} className="h-8 w-7" />
                        ) : (
                          ""
                        )}
                        <div
                          className="flex flex-col cursor-pointer"
                          onClick={() => onNewsletterClick(file.id)}
                        >
                          <span
                            className={`text-gray-400 text-[14px] font-[500] text-sm break-all line-clamp-1 ${
                              file?.name ? "text-primary" : ""
                            }`}
                          >
                            {file?.name || ""}
                          </span>
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              ) : (
                <div className="flex item-center justify-center text-[12px] h-[130px]">
                  <img src={images.no_newsletter} />
                </div>
              )}
            </div>

            <hr className="my-5" />
            <div className="grid grid-cols-1 gap-[20px]">
              {cards?.map((data: any, index: number) => {
                if (data?.hide) return;
                const isBudgetCard = data?.id == "budget";
                const expenseData = cards?.find(
                  (val: any) => val.id == "expense"
                );
                const receivables = cards?.find(
                  (val: any) => val.id == "receivables"
                );
                const budgetedData = [
                  {
                    ...expenseData,
                    title: "Expense",
                  },
                  {
                    ...receivables,
                    title: "Receivables",
                  },
                ];

                return (
                  <Card
                    key={`cards-${index}`}
                    className={cn(data?.className, "border-none p-0")}
                  >
                    {isBudgetCard ? (
                      <CardContent
                        className={cn(
                          "grid grid-cols-1 gap-[10px] text-white p-4"
                        )}
                      >
                        <div className="flex flex-row md:flex-col lg:flex-row items-center justify-between">
                          <div className="flex flex-row items-center gap-3">
                            <BadgeDollarSign className="h-8 w-8" />
                            <span className="text-[16px] md:text-[12px] lg:text-[16px]">
                              {currentFinanceYear} {data?.title}
                            </span>
                          </div>
                          <img
                            src={dollarIconWhite}
                            alt="bom-position"
                            className="block md:hidden lg:block"
                          />
                        </div>
                        <div className="flex flex-col gap-3">
                          {budgetedData?.map((bData: any, index: number) => {
                            return (
                              <div
                                className="flex flex-row md:flex-col lg:flex-row gap-3 items-center md:gap-1 md:items-start lg:gap-3 lg:items-center"
                                key={`b-data-${index}`}
                              >
                                <span className="font-[200] text-[14px] col-span-2 w-[90px]">
                                  {bData?.title || ""}:
                                </span>
                                <div className="flex flex-row md:flex-col lg:flex-row col-span-2 justify-end md:justify-start lg:justify-end">
                                  <span className="text-[20px] font-[500]">
                                    {formatCurrencyShort(
                                      Number(bData?.amount?.value || "0")
                                    )}
                                  </span>
                                  {bData?.amount?.value ? (
                                    <span
                                      className={cn(
                                        "text-[8px] flex flex-row items-center",
                                        bData?.prevData?.progress == "up"
                                          ? "text-green-400"
                                          : "text-red-600"
                                      )}
                                    >
                                      {bData?.prevData?.progress == "up" ? (
                                        <TrendingUp className="ml-2 mr-1 h-3 w-3" />
                                      ) : (
                                        <TrendingDown className="ml-2 mr-1 h-3 w-3" />
                                      )}{" "}
                                      {bData?.prevData?.value || ""}{" "}
                                      {Number(currentFinanceYear) - 1}
                                    </span>
                                  ) : (
                                    <></>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="grid grid-cols-1 text-[12px]">
                          <div className="flex flex-row md:flex-col lg:flex-row gap-2">
                            <span>Approved On:</span>
                            <span>
                              {prevYearsCounts?.data
                                ?.currentYearBudgetApprovalDate
                                ? moment(
                                    prevYearsCounts?.data
                                      ?.currentYearBudgetApprovalDate
                                  ).format("MM/DD/YYYY")
                                : "-"}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    ) : (
                      <CardContent
                        className={cn(
                          "grid grid-cols-1 gap-[10px] text-black p-3"
                        )}
                      >
                        <div className="flex flex-row items-center justify-between">
                          <span className="text-[18px] md:text-[14px] lg:text-[18px]">
                            {data?.title}
                          </span>
                          <img
                            src={dollarIcon}
                            alt="bom-position"
                            className="block md:hidden lg:block"
                          />
                        </div>
                        <div className="flex flex-row items-center">
                          <span className="text-[30px] font-[600]">
                            {formatCurrencyShort(data?.actual?.value || "0")}
                          </span>
                          {data?.actual?.value ? (
                            <span
                              className={cn(
                                "text-[10px] flex flex-row items-center",
                                data?.prevData?.progress == "up"
                                  ? "text-green-600"
                                  : "text-red-600"
                              )}
                            >
                              {data?.prevData?.progress == "up" ? (
                                <TrendingUp className="ml-2 mr-1 h-3 w-3" />
                              ) : (
                                <TrendingDown className="ml-2 mr-1 h-3 w-3" />
                              )}{" "}
                              {data?.prevData?.value || ""}{" "}
                              {Number(currentFinanceYear) - 1}
                            </span>
                          ) : (
                            <></>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
          {/* <ApprovalSettingsDtlCard /> */}
        </>
      )}
    </div>
  );
};

export default NewsLetterCard;
